import { useCallback, useEffect, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { TerracePreference } from "@/types/table";
import { isSelectedPlacementUnavailable, type PlacementAvailability } from "@/lib/publicAvailability";

interface UsePublicPlacementSelectionOptions {
  setTerracePreference: Dispatch<SetStateAction<TerracePreference>>;
  setZonePreference: Dispatch<SetStateAction<string[]>>;
  clearSelectedSlot: () => void;
  resetLoadedSlot?: () => void;
}

/**
 * Shared public-flow transitions for placement changes.
 * Placement changes preserve the date and party, but immediately invalidate
 * the previously selected time so it cannot survive a recalculation.
 */
export function usePublicPlacementSelection({
  setTerracePreference,
  setZonePreference,
  clearSelectedSlot,
  resetLoadedSlot,
}: UsePublicPlacementSelectionOptions) {
  const clearTimeSelection = useCallback(() => {
    clearSelectedSlot();
    resetLoadedSlot?.();
  }, [clearSelectedSlot, resetLoadedSlot]);

  const changeTerracePreference = useCallback((value: TerracePreference) => {
    clearTimeSelection();
    setTerracePreference(value);
  }, [clearTimeSelection, setTerracePreference]);

  const changeZonePreference = useCallback((value: string[]) => {
    clearTimeSelection();
    setZonePreference(value);
  }, [clearTimeSelection, setZonePreference]);

  return { changeTerracePreference, changeZonePreference, clearTimeSelection };
}

interface UseSelectedPlacementGuardOptions {
  terracePreference: TerracePreference;
  zonePreference: string[];
  placementAvailability: Partial<Record<string, PlacementAvailability>>;
  clearTimeSelection: () => void;
}

/**
 * When the customer's chosen placement becomes unavailable (date, party,
 * children or stroller changed), the choice is KEPT and only the selected time
 * is cleared. Callers hide the time grid while `selectedPlacementUnavailable`
 * is true. Never rewrite the placement here: a silent fallback to
 * 'no_preference' let the assignment engine place customers on the terrace.
 */
export function useSelectedPlacementGuard({
  terracePreference,
  zonePreference,
  placementAvailability,
  clearTimeSelection,
}: UseSelectedPlacementGuardOptions) {
  const selectedPlacementUnavailable = useMemo(
    () => isSelectedPlacementUnavailable(terracePreference, zonePreference, placementAvailability),
    [terracePreference, zonePreference, placementAvailability],
  );

  useEffect(() => {
    if (selectedPlacementUnavailable) clearTimeSelection();
  }, [selectedPlacementUnavailable, clearTimeSelection]);

  return { selectedPlacementUnavailable };
}
